import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Select, Spin, Typography, message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { getBulkMailDetails, sendBulkMail } from '../../services/dropdownApi';
import { getModuleDataDetail } from '../../services/detailedViewApi';
import { searchJobs } from '../../services/popupApi';
import CommonMailComposer from './CommonMailComposer';
import { areValidEmails, parseEmails } from './mailUtils';

const { Text } = Typography;
const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

function isEnabled(value) {
  if (value === true) return true;
  if (typeof value === 'number') return value === 1;
  if (typeof value !== 'string') return false;
  return ['true', '1', 'yes', 'y', 'on'].includes(value.trim().toLowerCase());
}

function isCandidateModule(moduleName) {
  return ['candidate', 'candidates'].includes(String(moduleName ?? '').trim().toLowerCase());
}

function pickValue(record, ...keys) {
  for (const key of keys) {
    const value = String(key).split('.').reduce((current, part) => current?.[part], record);
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  return undefined;
}

function optionLabel(job) {
  const reference = pickValue(job, 'jobReferenceId', 'clientJobId', 'jobCode', 'reqId', 'jobId');
  const title = pickValue(job, 'jobTitle', 'title', 'name');
  return [reference, title].filter(Boolean).join(' - ') || String(pickValue(job, '_id', 'id') ?? 'Job');
}

function normalizeJobOption(job) {
  const value = pickValue(job, '_id', 'id', 'jobId');
  if (!value) return null;
  return { value: String(value), label: optionLabel(job), raw: job };
}

function collectRecordEmails(records = [], fields = []) {
  const fieldList = [
    ...fields,
    'email',
    'candidateEmail',
    'candidate_email',
    'emailId',
    'mailId',
    'alternateEmail',
    'altEmail',
    'basicDetails.email',
    'basicDetails.alternateEmail',
    'basicDetails.altEmail',
    'candidate.email',
    'candidate.emailId',
    'submission.email',
    'submission.candidateEmail',
  ].filter(Boolean);

  return records.flatMap((record) => fieldList.map((field) => pickValue(record, field)))
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .flatMap(parseEmails);
}

function unwrapRecord(response) {
  return response?.data?.record
    ?? response?.record
    ?? response?.data
    ?? response;
}

function pickJobIdFromRecord(record) {
  const value = pickValue(
    record,
    'jobId',
    'job_id',
    'job._id',
    'job.id',
    'jobs._id',
    'jobs.id',
  );
  if (typeof value === 'object') return pickValue(value, '_id', 'id', '$oid');
  return value;
}

function normalizeMailDetails(data = {}) {
  return {
    subject: data.subject ?? data.Subject ?? '',
    contents: data.contents ?? data.Contents ?? data.body ?? data.Body ?? '',
    toMail: data.toMail ?? data.ToMail ?? [],
    ccMail: data.ccMail ?? data.CcMail ?? [],
    bccMail: data.bccMail ?? data.BccMail ?? [],
  };
}

export default function MailPopup() {
  const navigate = useNavigate();
  const { state = {} } = useLocation();
  const {
    selectedIds = [], selectedRecords = [], recipients = [],
    subject: initialSubject = '', onSend, moduleName = '', popupAction = {},
  } = state;
  const requireJobId = isEnabled(popupAction.requireJobId)
    || isEnabled(state.requireJobId)
    || isCandidateModule(moduleName);
  const recipientFields = useMemo(() => [
    popupAction.recipientField,
    popupAction.recipientPath,
    state.recipientField,
    state.recipientPath,
  ], [popupAction.recipientField, popupAction.recipientPath, state.recipientField, state.recipientPath]);
  const selectedIdKey = selectedIds.join('|');
  const initialRecipients = useMemo(() => [
    ...recipients,
    ...collectRecordEmails(selectedRecords, recipientFields),
  ], [recipients, selectedRecords, recipientFields]);
  const initialJobId = String(state.jobId ?? pickJobIdFromRecord(selectedRecords[0]) ?? '').trim();
  const legacyJobId = selectedIds[0] ?? selectedRecords[0]?._id ?? selectedRecords[0]?.id ?? '';
  const [jobIdInput, setJobIdInput] = useState(initialJobId);
  const [jobId, setJobId] = useState(requireJobId ? initialJobId : String(initialJobId || legacyJobId || ''));
  const [mail, setMail] = useState({
    to: [...new Set(initialRecipients)].join(', '), cc: '', bcc: '', subject: initialSubject,
    contents: '', showCc: false, showBcc: false,
  });
  const [detailsLoaded, setDetailsLoaded] = useState(!jobId);
  const [loadingJob, setLoadingJob] = useState(false);
  const [jobOptions, setJobOptions] = useState([]);
  const [jobSearchLoading, setJobSearchLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const recipientEditedRef = useRef(false);
  const detailRecipientPrefillKeyRef = useRef('');

  useEffect(() => {
    if (
      !isCandidateModule(moduleName)
      || recipientEditedRef.current
      || parseEmails(mail.to).length > 0
      || selectedIds.length === 0
      || detailRecipientPrefillKeyRef.current === selectedIdKey
    ) return;

    let cancelled = false;
    detailRecipientPrefillKeyRef.current = selectedIdKey;
    Promise.all(
      selectedIds.map((id) => getModuleDataDetail(moduleName, id).catch(() => null)),
    ).then((responses) => {
      if (cancelled) return;
      const detailRecords = responses.map(unwrapRecord).filter(Boolean);
      const detailEmails = collectRecordEmails(detailRecords, recipientFields);
      if (!detailEmails.length) return;
      setMail((current) => (
        parseEmails(current.to).length > 0
          ? current
          : { ...current, to: [...new Set(detailEmails)].join(', ') }
      ));
    });

    return () => { cancelled = true; };
  }, [mail.to, moduleName, recipientFields, selectedIdKey, selectedIds]);

  useEffect(() => {
    if (!jobId) {
      setDetailsLoaded(true);
      return;
    }
    setDetailsLoaded(false);
    setLoadingJob(true);
    getBulkMailDetails(jobId)
      .then((data) => {
        const details = normalizeMailDetails(data);
        setMail((current) => ({
          ...current,
          subject: details.subject || current.subject,
          to: details.toMail?.length ? (Array.isArray(details.toMail) ? details.toMail.join(', ') : String(details.toMail)) : current.to,
          cc: details.ccMail?.length ? (Array.isArray(details.ccMail) ? details.ccMail.join(', ') : String(details.ccMail)) : current.cc,
          bcc: details.bccMail?.length ? (Array.isArray(details.bccMail) ? details.bccMail.join(', ') : String(details.bccMail)) : current.bcc,
          contents: details.contents || current.contents,
          showCc: current.showCc || !!details.ccMail?.length,
          showBcc: current.showBcc || !!details.bccMail?.length,
        }));
      })
      .catch((error) => {
        if (requireJobId) message.error(error?.message || 'Could not load mail template for this Job ID');
      })
      .finally(() => { setDetailsLoaded(true); setLoadingJob(false); });
  }, [jobId, requireJobId]);

  function loadJobDetails(rawJobId = jobIdInput) {
    const nextJobId = String(rawJobId ?? '').trim();
    if (!OBJECT_ID_RE.test(nextJobId)) {
      setJobId('');
      if (nextJobId) message.warning('Enter a valid 24-character Job ID');
      return;
    }
    if (nextJobId === jobId) {
      // Allow an explicit reload even when the same job is entered again.
      setJobId('');
      queueMicrotask(() => setJobId(nextJobId));
      return;
    }
    setJobId(nextJobId);
  }

  function handleJobSelect(nextJobId, option) {
    if (!nextJobId) {
      setJobId('');
      setJobIdInput('');
      return;
    }
    const selectedLabel = option?.label ?? '';
    setJobIdInput(selectedLabel ? String(selectedLabel) : String(nextJobId ?? ''));
    loadJobDetails(nextJobId);
  }

  function handleJobSearch(search) {
    const term = String(search ?? '').trim();
    setJobIdInput(term);
    if (term.length < 3) {
      setJobOptions([]);
      return;
    }

    setJobSearchLoading(true);
    searchJobs(term)
      .then((jobs) => {
        const options = (Array.isArray(jobs) ? jobs : [])
          .map(normalizeJobOption)
          .filter(Boolean);
        setJobOptions(options);
      })
      .catch((error) => message.error(error?.message || 'Unable to search jobs'))
      .finally(() => setJobSearchLoading(false));
  }

  const canSend = detailsLoaded
    && (!requireJobId || OBJECT_ID_RE.test(jobId))
    && (parseEmails(mail.to).length + parseEmails(mail.cc).length + parseEmails(mail.bcc).length) > 0
    && areValidEmails(mail.to) && areValidEmails(mail.cc) && areValidEmails(mail.bcc) && mail.subject.trim() && mail.contents.trim();

  async function handleSend() {
    if (!canSend || sending) return;
    setSending(true);
    try {
      await sendBulkMail({ jobId, toMail: parseEmails(mail.to), ccMail: parseEmails(mail.cc), bccMail: parseEmails(mail.bcc), subject: mail.subject, contents: mail.contents });
      message.success('Mail sent successfully');
      if (typeof onSend === 'function') onSend({ ...mail, message: mail.contents, selectedIds });
      navigate(-1);
    } catch (error) {
      message.error(error?.message || 'Failed to send mail');
    } finally { setSending(false); }
  }

  function handleMailChange(nextMail) {
    if (nextMail?.to !== mail.to) recipientEditedRef.current = true;
    setMail(nextMail);
  }

  return (
    <Drawer open placement="right" onClose={() => navigate(-1)} closable={false} rootClassName="mail-popup" width={620} maskClosable={false}
      footer={<div className="mail-popup__footer"><Button onClick={() => navigate(-1)}>Cancel</Button><Button type="primary" loading={sending} disabled={!canSend} onClick={handleSend}>Send</Button></div>}>
      <CommonMailComposer
        value={mail}
        onChange={handleMailChange}
        moduleName={moduleName}
        defaultTemplateId={requireJobId ? '' : (popupAction.defaultTemplateId ?? state.defaultTemplateId ?? '')}
        ready={detailsLoaded}
        contextRecords={selectedRecords}
        beforeFields={requireJobId ? (
          <div className="mail-popup__job-row">
            <Text type="secondary" className="mail-popup__field-label">Job Title / ID</Text>
            <Select
              showSearch
              allowClear
              filterOption={false}
              value={jobId || undefined}
              searchValue={jobIdInput}
              onSearch={handleJobSearch}
              onChange={handleJobSelect}
              onClear={() => {
                setJobId('');
                setJobIdInput('');
                setJobOptions([]);
              }}
              placeholder="Minimum 3 characters"
              notFoundContent={jobSearchLoading || loadingJob ? <Spin size="small" /> : null}
              options={jobOptions}
              className="mail-popup__job-input"
            />
          </div>
        ) : null}
      />
    </Drawer>
  );
}
